import { throttle } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import Formula from "fparser";

import {
  IAggregatedConstructor,
  IAggregatedData,
  useAggregatedConstructor,
} from "@/hooks/useAggregatedConstructor";
import { PriceData, useBinancePrices } from "@/hooks/useBinancePrices";
import { IMarketRatio, useMarketRatioAll } from "@/hooks/useMarketRatioAll";
import { IFavoriteFolder } from "@/domain/interfaces/dashboard.interface";
import { applyFilters, prepareFormula } from "@/lib/utils";
import { IParam } from "@/domain/interfaces/general.interface";
import { useDashboard } from "@/lib/store";

type IFolderRow = IFavoriteFolder & {
  isFolder: boolean;
};
type ISymbolRow = IAggregatedConstructor & {
  name: string;
  folderId: string | null;
  isPinned: boolean;
  folderColor?: string;
  currentPrice: PriceData;
  marketRatio: IMarketRatio;
};
type IFavoritesRow = Partial<IFolderRow & ISymbolRow>;

const REFRESH_INTERVAL = 2000; // 2 seconds

function getPrice(
  prices: Record<string, PriceData> | undefined,
  symbol: string,
) {
  if (!prices || !symbol) {
    return;
  }

  const formula = prepareFormula(symbol);
  const calculator = new Formula(formula);
  const variables = calculator.getVariables();
  const priceParams: IParam = {};

  variables.forEach((variable) => {
    priceParams[variable] = 0;
  });

  for (const key in prices) {
    priceParams[key] = prices[key].price;
  }

  const price = calculator.evaluate(priceParams);

  return {
    priceChange: 0,
    priceChangePrc: 0,
    closeQty: 0,
    baseVolume: 0,
    quoteVolume: 0,
    price: Array.isArray(price) ? Number(price[0]) : Number(price),
  };
}

function getVolumeQuote(
  aggregatedData: IAggregatedData | undefined,
  symbol: string,
) {
  if (!aggregatedData) {
    return {
      "15m": 0,
      "30m": 0,
      "1h": 0,
      "4h": 0,
      "1d": 0,
      "1w": 0,
      "1M": 0,
    } as IAggregatedConstructor["volumeQuote"];
  }

  const usdtCount = symbol.split("/").filter((s) => s === "USDT").length;
  if (usdtCount > 1) {
    return {
      "15m": 0,
      "30m": 0,
      "1h": 0,
      "4h": 0,
      "1d": 0,
      "1w": 0,
      "1M": 0,
    } as IAggregatedConstructor["volumeQuote"];
  }
  return aggregatedData[symbol]?.volumeQuote;
}

export function useFavorites() {
  const { favorites, pinned, globalFilters, getGlobalFilterValues, blacklist } =
    useDashboard((state) => ({
      favorites: state.favorites,
      pinned: state.pinned,
      globalFilters: state.globalFilters,
      getGlobalFilterValues: state.getGlobalFilterValues,
      blacklist: state.blacklist,
    }));

  const uniqueSymbolNames = useMemo(() => {
    const symbolSet = new Set<string>();

    for (const item of favorites) {
      for (const symbol of item.symbols) {
        if (
          symbol?.includes("/") ||
          symbol?.includes("+") ||
          symbol?.includes("-") ||
          symbol?.includes("*")
        ) {
          symbol
            ?.split(/[/+\-*]/)
            .forEach((part) => symbolSet.add(part.trim()));
        } else {
          symbolSet.add(symbol);
        }
      }
    }

    for (const symbol of pinned) {
      if (
        symbol?.includes("/") ||
        symbol?.includes("+") ||
        symbol?.includes("-") ||
        symbol?.includes("*")
      ) {
        symbol?.split(/[/+\-*]/).forEach((part) => symbolSet.add(part.trim()));
      } else {
        symbolSet.add(symbol);
      }
    }

    return Array.from(symbolSet);
  }, [favorites, pinned]);

  const { data: aggregatedData } = useAggregatedConstructor(uniqueSymbolNames);
  const { data: prices } = useBinancePrices(REFRESH_INTERVAL);
  const { data: marketRatio } = useMarketRatioAll(uniqueSymbolNames);

  // useEffect(() => {
  //   console.log("aggregatedData changed:", aggregatedData);
  // }, [aggregatedData]);

  const processSymbol = useCallback(
    (symbol: string, folder?: IFavoriteFolder) => ({
      ...aggregatedData?.[symbol],
      volumeQuote: getVolumeQuote(aggregatedData, symbol),
      volume15m: aggregatedData?.[symbol]?.volumeQuote["15m"] ?? 0,
      volume30m: aggregatedData?.[symbol]?.volumeQuote["30m"] ?? 0,
      volume1h: aggregatedData?.[symbol]?.volumeQuote["1h"] ?? 0,
      volume4h: aggregatedData?.[symbol]?.volumeQuote["4h"] ?? 0,
      volume1d: aggregatedData?.[symbol]?.volumeQuote["1d"] ?? 0,
      volume1w: aggregatedData?.[symbol]?.volumeQuote["1w"] ?? 0,
      volume1M: aggregatedData?.[symbol]?.volumeQuote["1M"] ?? 0,
      adtv15m: aggregatedData?.[symbol]?.adtv["15m"] ?? 0,
      adtv30m: aggregatedData?.[symbol]?.adtv["30m"] ?? 0,
      adtv1h: aggregatedData?.[symbol]?.adtv["1h"] ?? 0,
      adtv4h: aggregatedData?.[symbol]?.adtv["4h"] ?? 0,
      adtv1d: aggregatedData?.[symbol]?.adtv["1d"] ?? 0,
      adtv1w: aggregatedData?.[symbol]?.adtv["1w"] ?? 0,
      adtv1M: aggregatedData?.[symbol]?.adtv["1M"] ?? 0,
      currentPrice: getPrice(prices, symbol),
      marketRatio: marketRatio && marketRatio[symbol],
      name: symbol,
      folderId: folder?.id ?? null,
      isPinned: !folder,
      folderColor: folder?.color,
    }),
    [aggregatedData, marketRatio, prices],
  );

  const data: IFavoritesRow[] = useMemo(() => {
    const result: IFavoritesRow[] = [];

    // Add pinned items
    for (const symbol of pinned) {
      result.push(processSymbol(symbol));
    }

    // Add folders and their symbols
    for (const folder of favorites) {
      result.push({
        ...folder,
        isFolder: true,
        name: folder.name,
      });

      if (folder.expanded) {
        for (const symbol of folder.symbols) {
          const processedSymbol = processSymbol(symbol, folder);
          result.push(processedSymbol);
        }
      }
    }

    const preparedData = applyFilters(
      result,
      globalFilters,
      getGlobalFilterValues(),
    );
    return preparedData;
  }, [favorites, getGlobalFilterValues, globalFilters, pinned, processSymbol]);

  return data;
}
